import React, { useCallback, useEffect, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { MathUtils, Vector3 } from 'three';
import { Dict } from '../../utils/Dict';
import { SectionPosition } from './SectionPosition';
import { DetailSectionIDs } from '../../data/Sections';
import { useFocusContext } from './context/FocusContext';
import gsap from 'gsap';
import { useDetailViewContext } from '../../context/DetailViewContext';

type Props = {
    targets: Dict<SectionPosition>,
    mouseMovement?: number,
    smoothing?: number
}

function getCamProps(def: SectionPosition)
{
  const pos = new Vector3().addVectors(def.sectionPosition, def.cameraOffset);
  const lookAt = def.sectionPosition.clone();
  if (def.cameraLookAtOffset) {
    lookAt.add(def.cameraLookAtOffset);
  }
  return { pos, lookAt };
}

const p = new Vector3();
const focusOffset = new Vector3(0.2, 0.4, 1).normalize();

export default function CameraController({ targets, mouseMovement, smoothing }: Props): JSX.Element
{
  const { camera, gl } = useThree();
  const mouseOffset = useMemo(() => new Vector3(), []);
  const mouseOffsetTarget = useMemo(() => new Vector3(), []);
  const { getDetailIndexForID, detailIndex, maxDetailIndex } = useDetailViewContext();
  const { focusPoint } = useFocusContext();
  // when focusPoint turns undefined, we still need the previous point for tweening
  const storedFocusPoint = useMemo(() =>
  {
    return { position: new Vector3(), size: 0 };
  }, []);
  const focusTween = useMemo(() =>
  {
    return { value: 0 };
  }, []);

  // This controls zooming into focus points (dark mode)
  useEffect(() =>
  {
    gsap.killTweensOf(focusTween);
    gsap.to(focusTween, { value: focusPoint ? 1 : 0, duration: 1 });

    // if focusPoint === undefined, storedFocusPoint will contain the old value for tweening
    if (!focusPoint) return;

    // already zoomed in (or halfway, so let's tween)
    focusPoint.target.getWorldPosition(p);

    if (focusTween.value > 0) {
      gsap.to(storedFocusPoint.position, {
        x: p.x,
        y: p.y,
        z: p.z,
        duration: 1
      });
      gsap.to(storedFocusPoint, { size: focusPoint.size, duration: 1 });
    }
    else {
      storedFocusPoint.position.copy(p);
      storedFocusPoint.size = focusPoint.size;
    }

  }, [ focusPoint, focusTween, storedFocusPoint ]);

  // this is an example on how to use a single global timeline based on the transition timings
  const camOrientations = useMemo(() =>
  {
    const pos = [];
    pos[getDetailIndexForID(DetailSectionIDs.DecodingVector)] = getCamProps(targets.VectorsOnly);
    pos[getDetailIndexForID(DetailSectionIDs.DecodingSphere)] = getCamProps(targets.VectorsToSpheres);
    pos[getDetailIndexForID(DetailSectionIDs.DecodingSubspaces)] = getCamProps(targets.SphereSubspaces);
    pos[getDetailIndexForID(DetailSectionIDs.DecodingTopology)] = getCamProps(targets.TopologicalRelations);
    pos[getDetailIndexForID(DetailSectionIDs.DecodingDynamicBehaviour)] = getCamProps(targets.DynamicBehavior);
    return pos;
  }, [ getDetailIndexForID, targets ]);

  useFrame(() =>
  {
    const i1 = Math.max(Math.floor(detailIndex), 0);
    const i2 = Math.min(i1 + 1, maxDetailIndex);

    const fr = detailIndex - Math.floor(detailIndex);
    mouseOffset.x = MathUtils.lerp(mouseOffsetTarget.x, mouseOffset.x, smoothing || 0.1);
    mouseOffset.y = MathUtils.lerp(mouseOffsetTarget.y, mouseOffset.y, smoothing || 0.1);

    camera.position.copy(camOrientations[i1].pos);
    camera.position.lerp(camOrientations[i2].pos, fr);

    if (focusTween.value > 0) {
      p.copy(storedFocusPoint.position).addScaledVector(focusOffset, storedFocusPoint.size + 0.5);
      camera.position.lerp(p, focusTween.value);
    }

    camera.position.addScaledVector(mouseOffset, mouseMovement || 0);

    // p is now lookat target
    p.copy(camOrientations[i1].lookAt);
    p.lerp(camOrientations[i2].lookAt, fr);

    if (focusTween.value > 0) {
      p.lerp(storedFocusPoint.position, focusTween.value);
      camera.lookAt(p);
    }
    else {
      camera.lookAt(p);
    }
  });

  const onMouseMove = useCallback((event: MouseEvent) =>
  {
    mouseOffsetTarget.x = event.clientX / window.innerWidth * 2.0 - 1.0;
    mouseOffsetTarget.y = event.clientY / window.innerHeight * 2.0 - 1.0;
  }, [ mouseOffsetTarget ]);

  useEffect(() =>
  {
    document.addEventListener('mousemove', onMouseMove);
    return () =>
    {
      document.removeEventListener('mousemove', onMouseMove);
    };
  }, [ gl, onMouseMove ]);

  return <></>;
}
